import { useEffect, useId, useRef, useState } from 'react';
import {
  userErrorMessage,
  EBOOK_TIME_LIMIT_SECONDS,
  type EbookAttempt,
  type EbookLearningProgress,
  type EbookQuestion,
  type EbookRelease,
  type EbookWorkspace,
} from '@wx-weiqi/contracts';
import { EbookClient } from '../services/ebooks';
import { EbookAnswerPage } from './EbookAnswerPage';
import { EbookPracticeWorkspace } from './EbookPracticeWorkspace';
import './ebook-question-card.css';
import {
  continueQuestion,
  learningQuestionIds,
  type EbookLearningFilter,
} from '../services/ebook-learning';
import {
  ebookChapterNames,
  ebookChapterQuestionIds,
  normalizeEbookChapter,
} from '../services/ebook-chapters';
export const ebookStatus: Record<string, string> = {
  CONTINUE: '请继续作答',
  CORRECT: '回答正确 ✅',
  INCORRECT: '回答错误 ❌',
  UNKNOWN: '未收录此变化 / 待核验',
  PENDING_REVIEW: '待教师批改 / 未自动判定',
};
export function EbookThumbnail({
  question: q,
  illustrated = false,
}: {
  question: Pick<EbookQuestion, 'title' | 'visible' | 'size' | 'stones'>;
  illustrated?: boolean;
}) {
  const id = useId().replace(/:/g, '');
  const v = q.visible,
    step = 20;
  const stones = q.stones.flatMap((s, i) => {
    const x = (i % q.size) - v.x,
      y = Math.floor(i / q.size) - v.y;
    return s && x >= 0 && y >= 0 && x < v.columns && y < v.rows ? [{ s, x, y }] : [];
  });
  return (
    <svg
      className="ebook-thumbnail"
      viewBox={`-12 -12 ${(v.columns - 1) * step + 24} ${(v.rows - 1) * step + 24}`}
      role="img"
      aria-label={`${q.title}结构化棋形缩略图`}
    >
      {illustrated && (
        <defs>
          <radialGradient id={`${id}-black`} cx="32%" cy="24%" r="76%">
            <stop offset="0" stopColor="#626c65" />
            <stop offset=".45" stopColor="#252c29" />
            <stop offset="1" stopColor="#101411" />
          </radialGradient>
          <radialGradient id={`${id}-white`} cx="30%" cy="22%" r="80%">
            <stop offset="0" stopColor="#fffefa" />
            <stop offset=".6" stopColor="#f4f1e8" />
            <stop offset="1" stopColor="#c9c7bd" />
          </radialGradient>
        </defs>
      )}
      {Array.from({ length: v.columns }, (_, i) => (
        <line
          key={`v${i}`}
          x1={i * step}
          x2={i * step}
          y1={0}
          y2={(v.rows - 1) * step}
          stroke="#87633a"
          strokeWidth=".6"
        />
      ))}
      {Array.from({ length: v.rows }, (_, i) => (
        <line
          key={`h${i}`}
          y1={i * step}
          y2={i * step}
          x1={0}
          x2={(v.columns - 1) * step}
          stroke="#87633a"
          strokeWidth=".6"
        />
      ))}
      {stones.map((p, i) => (
        <circle
          key={i}
          cx={p.x * step}
          cy={p.y * step}
          r={9}
          fill={
            illustrated
              ? `url(#${id}-${p.s === 'BLACK' ? 'black' : 'white'})`
              : p.s === 'BLACK'
                ? '#262629'
                : '#fffdf7'
          }
          stroke={p.s === 'BLACK' ? '#171719' : '#8b8b82'}
          strokeWidth=".7"
        />
      ))}
    </svg>
  );
}
export function EbookReader({
  client,
  releaseId,
  preview,
  initialQuestionId,
  onBack,
}: {
  client: EbookClient;
  releaseId?: string;
  initialQuestionId?: string;
  preview?: { documentId: string; data: EbookWorkspace };
  onBack: () => void;
}) {
  const [release, setRelease] = useState<EbookRelease | null>(null);
  const [readingRelease, setReadingRelease] = useState<EbookRelease | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Preview selection is deliberately separate from an authorized, timed attempt.
  const [previewQuestionId, setPreviewQuestionId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [progress, setProgress] = useState<EbookLearningProgress | null>(null);
  const [filter, setFilter] = useState<EbookLearningFilter>('ALL');
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
  const [queue, setQueue] = useState<string[]>([]);
  const summary = progress?.questions ?? [];
  const [active, setActive] = useState<EbookAttempt | null>(null);
  const [viewed, setViewed] = useState<{ board: EbookAttempt['board']; ply: number } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState<EbookAttempt['moves']>([]);
  const [trialBoard, setTrialBoard] = useState<EbookAttempt['board'] | null>(null);
  const [trialStatus, setTrialStatus] = useState<EbookAttempt['status']>('CONTINUE');
  const [trialSubmitted, setTrialSubmitted] = useState(false);
  const [trialReady, setTrialReady] = useState(false);
  const [trialDeadline, setTrialDeadline] = useState(0);
  const [trialFinished, setTrialFinished] = useState<number | null>(null);
  const [now, setNow] = useState(() => client.serverNow());
  const initialized = useRef(false);
  const inFlight = useRef(false);
  const lastSync = useRef(0);
  const scroll = useRef(0);
  const book = preview?.data.book ?? release?.book;
  const questions = preview?.data.questions ?? release?.questions ?? [];
  const loadedQuestionIds = new Set(questions.map((question) => question.id));
  const q = questions.find((p) => p.id === selected);
  const chapter =
    selectedChapter && book && !ebookChapterNames(book, questions).includes(selectedChapter)
      ? null
      : selectedChapter;
  const chapterIds = book ? ebookChapterQuestionIds(book, questions, chapter) : [];
  const visibleIds =
    !preview && !progress && filter !== 'ALL'
      ? []
      : learningQuestionIds(chapterIds, summary, filter);
  // An unfinished attempt stays recoverable even when it belongs to a different chapter.
  const continueId = continueQuestion(
    chapterIds.filter((id) => loadedQuestionIds.has(id)),
    progress,
  );
  const previewId =
    chapterIds.includes(previewQuestionId ?? '') && loadedQuestionIds.has(previewQuestionId ?? '')
      ? previewQuestionId
      : continueId && visibleIds.includes(continueId)
        ? continueId
        : (visibleIds.find((id) => loadedQuestionIds.has(id)) ?? null);
  const completed = preview ? trialSubmitted : active?.submitted === true;
  const ready = preview ? trialReady : active !== null;
  const deadline = preview
    ? trialDeadline
    : active?.timing
      ? Date.parse(active.timing.deadlineAt)
      : 0;
  const finished = preview
    ? trialFinished
    : active?.timing?.finishedAt
      ? Date.parse(active.timing.finishedAt)
      : null;
  const remaining = !deadline
    ? EBOOK_TIME_LIMIT_SECONDS
    : Math.max(
        0,
        Math.min(EBOOK_TIME_LIMIT_SECONDS, Math.ceil((deadline - (finished ?? now)) / 1000)),
      );

  const run = async (fn: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(userErrorMessage(e, '操作失败'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const refreshSummary = async () => {
    if (releaseId) setProgress(await client.learningProgress(releaseId));
  };
  const command = async (action: string, extra: Record<string, unknown> = {}) => {
    if (!active) return;
    await run(async () => {
      try {
        const next = await client.attempt(active.releaseId, {
          action,
          attemptId: active.id,
          expectedVersion: active.stateVersion,
          ...extra,
        });
        setActive(next);
        if (action === 'MOVE' || action === 'DEMONSTRATE') setViewed(null);
        setNow(client.serverNow());
        if (next.submitted) await refreshSummary();
      } catch (e) {
        // Recover a changed version (another tab or an acknowledged-but-lost response).
        const current = await client.ownAttempt(active.releaseId, active.id);
        if (current) setActive(current);
        throw e;
      }
    });
  };
  useEffect(() => {
    let alive = true;
    if (!releaseId) return;
    setBusy(true);
    setError('');
    void Promise.all([
      client.currentRelease(releaseId),
      client.reading(releaseId),
      client.learningProgress(releaseId),
    ])
      .then(([r, p, learning]) => {
        if (!alive) return;
        setRelease(r);
        setReadingRelease(r);
        setProgress(learning);
        scroll.current = p?.scrollY ?? 0;
        // Re-enter at the continuation controls; only returning from an answer restores the list.
        requestAnimationFrame(() => window.scrollTo(0, 0));
      })
      .catch((e) => {
        if (alive) setError(userErrorMessage(e, '读取失败'));
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [client, releaseId, reloadKey]);
  useEffect(() => {
    if (!releaseId || selected || !progress) return;
    let alive = true;
    const refresh = () => {
      if (inFlight.current) return;
      void client
        .learningProgress(releaseId)
        .then((next) => {
          if (alive) setProgress(next);
        })
        .catch(() => {
          if (alive) setError('学习进度暂未更新，请重试。');
        });
    };
    const nextDue = Math.min(
      ...progress.questions
        .filter((p) => p.reviewState === 'SCHEDULED' && p.nextReviewAt)
        .map((p) => Date.parse(p.nextReviewAt!)),
    );
    const timer = Number.isFinite(nextDue)
      ? window.setTimeout(refresh, Math.max(1000, nextDue - client.serverNow()))
      : null;
    window.addEventListener('focus', refresh);
    return () => {
      alive = false;
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [client, releaseId, selected, progress?.asOf]);
  const open = async (question: EbookQuestion, nextQueue?: string[]) => {
    if (busy || inFlight.current) return;
    if (!selected) scroll.current = window.scrollY;
    if (nextQueue) setQueue(nextQueue);
    setPreviewQuestionId(question.id);
    setSelected(question.id);
    setActive(null);
    setViewed(null);
    setTrial([]);
    setTrialBoard({ stones: question.stones, currentPlayer: question.currentPlayer });
    setTrialSubmitted(false);
    setTrialReady(false);
    setTrialFinished(null);
    requestAnimationFrame(() => window.scrollTo(0, 0));
    await run(async () => {
      if (preview) {
        const result = await client.practicePreview(preview.documentId, question, []);
        setTrial(result.moves);
        setTrialBoard(result.board);
        setTrialStatus(result.status);
        setTrialDeadline(client.serverNow() + EBOOK_TIME_LIMIT_SECONDS * 1000);
        setTrialReady(true);
      } else if (readingRelease) {
        // Reload metadata before START: another tab may have begun an attempt on an old release.
        const learning = await client.learningProgress(readingRelease.id);
        setProgress(learning);
        const pending = learning.questions.find((p) => p.questionId === question.id)?.pending;
        let a: EbookAttempt;
        if (pending) {
          const pinned =
            pending.releaseId === readingRelease.id
              ? readingRelease
              : await client.release(pending.releaseId);
          const current = await client.ownAttempt(pending.releaseId, pending.id);
          a = await client.attempt(pending.releaseId, {
            action: 'SYNC',
            attemptId: current.id,
            expectedVersion: current.stateVersion,
          });
          setRelease(pinned);
        } else {
          a = await client.attempt(readingRelease.id, { action: 'START', questionId: question.id });
          setRelease(readingRelease);
        }
        setActive(a);
        await client.saveReading(a.releaseId, question.id, Math.round(scroll.current));
        if (a.submitted) await refreshSummary();
      }
      setNow(client.serverNow());
    });
  };
  const resume = async () => {
    if (!continueId || busy) return;
    const question = questions.find((p) => p.id === continueId);
    if (question) {
      const targetChapter = chapterIds.includes(question.id)
        ? chapter
        : normalizeEbookChapter(question.chapter);
      const targetIds = book ? ebookChapterQuestionIds(book, questions, targetChapter) : [];
      const nextIds = learningQuestionIds(targetIds, summary, filter);
      setSelectedChapter(targetChapter);
      await open(question, nextIds.includes(question.id) ? nextIds : [question.id, ...nextIds]);
      return;
    }
    // A removed question remains resumable only through its authorized original snapshot.
    const pending = progress?.resume;
    if (!pending || pending.submitted) return;
    await run(async () => {
      const pinned = await client.release(pending.releaseId);
      const current = await client.ownAttempt(pending.releaseId, pending.id);
      const a = await client.attempt(pending.releaseId, {
        action: 'SYNC',
        attemptId: current.id,
        expectedVersion: current.stateVersion,
      });
      scroll.current = window.scrollY;
      setRelease(pinned);
      setPreviewQuestionId(pending.questionId);
      setSelected(pending.questionId);
      setActive(a);
      setViewed(null);
      setQueue([pending.questionId]);
      setNow(client.serverNow());
      requestAnimationFrame(() => window.scrollTo(0, 0));
    });
  };
  const back = () => {
    if (!completed || busy) return;
    if (initialQuestionId) {
      onBack();
      return;
    }
    void run(async () => {
      if (releaseId && release) {
        await client.saveReading(release.id, selected, Math.round(scroll.current));
        const latest = await client.currentRelease(releaseId);
        setRelease(latest);
        setReadingRelease(latest);
        await refreshSummary();
      }
      setSelected(null);
      setActive(null);
      setViewed(null);
      requestAnimationFrame(() => window.scrollTo(0, scroll.current));
    });
  };
  useEffect(() => {
    if (initialQuestionId && !initialized.current) {
      const first = questions.find((p) => p.id === initialQuestionId);
      if (first) {
        initialized.current = true;
        void open(first, book?.questionIds ?? []);
      }
    }
  }, [initialQuestionId]);
  useEffect(() => {
    if (!q) return;
    const timer = window.setInterval(() => setNow(client.serverNow()), 250);
    return () => window.clearInterval(timer);
  }, [client, q?.id]);
  useEffect(() => {
    if (!q || !ready || completed || busy || remaining > 0) return;
    if (preview) {
      setTrialStatus(q.mode === 'AUTO' ? 'INCORRECT' : 'PENDING_REVIEW');
      setTrialSubmitted(true);
      setTrialFinished(trialDeadline);
    } else if (now - lastSync.current >= 2000) {
      lastSync.current = now;
      void command('SYNC');
    }
  }, [now, busy, completed, ready, remaining, q?.id]);
  // Background tabs keep their deadline; returning synchronizes state before any further move.
  useEffect(() => {
    const sync = () => {
      if (!preview && active && !active.submitted && !inFlight.current) void command('SYNC');
    };
    window.addEventListener('focus', sync);
    return () => window.removeEventListener('focus', sync);
  }, [active, preview]);
  const move = async (point: { x: number; y: number } | null) => {
    if (!q || !ready || completed || remaining <= 0 || busy || viewed) return;
    if (!preview) {
      await command('MOVE', {
        move: { color: active?.board.currentPlayer ?? q.currentPlayer, point },
      });
      return;
    }
    await run(async () => {
      const result = await client.practicePreview(preview.documentId, q, trial, {
        color: trialBoard?.currentPlayer ?? q.currentPlayer,
        point,
      });
      setTrial(result.moves);
      setTrialBoard(result.board);
      setTrialStatus(result.status);
      if (q.mode === 'AUTO' && result.status !== 'CONTINUE') {
        setTrialSubmitted(true);
        setTrialFinished(client.serverNow());
      }
    });
  };
  const navigateMove = async (ply: number) => {
    if (!q || busy || !ready) return;
    const total = (active?.moves ?? trial).length;
    if (ply < 0 || ply > total) return;
    if (ply === total) {
      setViewed(null);
      return;
    }
    await run(async () => {
      if (preview) {
        const result = await client.practicePreview(preview.documentId, q, trial.slice(0, ply));
        setViewed({ board: result.board, ply });
      } else if (active) {
        const result = await client.attemptPosition(active.releaseId, active.id, ply);
        setViewed({ board: result.board, ply: result.ply });
      }
    });
  };
  if (!book)
    return (
      <section className="ebook-page">
        <button onClick={onBack}>返回书架</button>
        <p role={error ? 'alert' : 'status'}>{error || '正在打开电子书…'}</p>
        {error && (
          <button disabled={busy} onClick={() => setReloadKey((value) => value + 1)}>
            重试加载
          </button>
        )}
      </section>
    );
  if (q)
    return (
      <EbookAnswerPage
        book={book}
        question={q}
        position={
          viewed?.board ??
          active?.board ??
          trialBoard ?? { stones: q.stones, currentPlayer: q.currentPlayer }
        }
        moves={(active?.moves ?? trial).slice(0, viewed?.ply)}
        history={{
          ply: viewed?.ply ?? (active?.moves ?? trial).length,
          total: (active?.moves ?? trial).length,
          onNavigate: (ply) => void navigateMove(ply),
        }}
        ready={ready}
        remaining={remaining}
        completed={completed}
        busy={busy}
        preview={!!preview}
        message={`${ebookStatus[preview ? trialStatus : (active?.status ?? 'CONTINUE')]}${completed ? ' · 已完成' : ''}${active?.feedback ? ' · ' + active.feedback : ''}`}
        status={preview ? trialStatus : (active?.status ?? 'CONTINUE')}
        feedback={preview ? '' : (active?.feedback ?? '')}
        error={error}
        onPoint={(p) => void move(p)}
        onReturn={back}
        hasNext={queue.indexOf(q.id) >= 0 && queue.indexOf(q.id) < queue.length - 1}
        onNext={() => {
          if (!completed || busy) return;
          const next = (preview?.data.questions ?? readingRelease?.questions ?? questions).find(
            (p) => p.id === queue[queue.indexOf(q.id) + 1],
          );
          if (next) void open(next);
        }}
        controls={
          <>
            {!ready && !busy && <button onClick={() => void open(q)}>重试加载本题</button>}
            {q.mode === 'MANUAL' && (
              <button
                disabled={
                  busy ||
                  !ready ||
                  completed ||
                  viewed !== null ||
                  (active?.moves ?? trial).length === 0
                }
                onClick={() => {
                  if (preview) {
                    setTrialSubmitted(true);
                    setTrialFinished(client.serverNow());
                  } else void command('SUBMIT');
                }}
              >
                提交作答
              </button>
            )}
          </>
        }
      />
    );
  return (
    <EbookPracticeWorkspace
      book={book}
      questions={questions}
      progress={progress}
      chapter={chapter}
      onChapter={(nextChapter) => {
        setSelectedChapter(nextChapter);
        const nextIds = ebookChapterQuestionIds(book, questions, nextChapter);
        setPreviewQuestionId(learningQuestionIds(nextIds, summary, filter)[0] ?? null);
      }}
      filter={filter}
      visibleIds={visibleIds}
      selectedId={previewId}
      onSelect={setPreviewQuestionId}
      onFilter={(nextFilter) => {
        setFilter(nextFilter);
        setPreviewQuestionId(learningQuestionIds(chapterIds, summary, nextFilter)[0] ?? null);
      }}
      onStart={(question) => {
        // Keep a completed review in preview even after it leaves the due list.
        const nextQueue = visibleIds.includes(question.id)
          ? visibleIds
          : [question.id, ...visibleIds];
        void open(question, nextQueue);
      }}
      onBack={onBack}
      busy={busy}
      error={error}
      preview={!!preview}
      continueId={continueId}
      onContinue={() => void resume()}
      onRetry={() => setReloadKey((value) => value + 1)}
    />
  );
}
